// ============================================================================
// AddSubcontractorCard — propose_add_subcontractor (prototype screen 9, item #683).
// The card IS the read-back of the plan: who, which markets, what kind of work,
// and that they go in the pipeline as a Lead — never straight to Bench for
// someone who has not applied. "Do it" calls the one door (add_subcontractor);
// for a Lead the card then shows the same send screen as + Add — the text app,
// or name / phone / link to copy into any app (item 869). Nightly, project work
// or both. It promises no later follow-up.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { marketOptions, useMarkets } from '../../../shared/markets';
import { CardShell } from './card-shell';
import { applicationLinkFor, workKindFrom, type WorkKind } from '../../subcontractors/hub/actions';
import { SendApplicationChoice, type SendApplicationTarget } from '../../subcontractors/hub/send-application';
import { errorMessage } from '../../../shared/errors';
import { AlreadyOnFileNotice, useOnFileFor } from '../../subcontractors/already-on-file';

export function AddSubcontractorCard({
  proposal,
  isCommitting,
  onDoIt,
  onDiscard,
}: {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}) {
  const initial = proposal.input as {
    name?: string;
    phone?: string | null;
    coverage_markets?: string[] | null;
    kind?: WorkKind | null;
    stage?: 'Lead' | 'Meet' | null;
  };
  const { markets } = useMarkets();
  const [name, setName] = useState(initial.name ?? '');
  const [phone, setPhone] = useState(initial.phone ?? '');
  const [chosen, setChosen] = useState<string[]>(initial.coverage_markets ?? []);
  const initialKind: WorkKind = initial.kind === 'both' || initial.kind === 'projects' ? initial.kind : 'nightly';
  const [nightly, setNightly] = useState(initialKind !== 'projects');
  const [projects, setProjects] = useState(initialKind !== 'nightly');
  const kind = workKindFrom(nightly, projects);
  const stage: 'Lead' | 'Meet' = initial.stage === 'Meet' ? 'Meet' : 'Lead';

  const wasEdited = useMemo(
    () =>
      name !== (initial.name ?? '') ||
      phone !== (initial.phone ?? '') ||
      kind !== initialKind ||
      chosen.join('|') !== (initial.coverage_markets ?? []).join('|'),
    [name, phone, kind, chosen, initial],
  );

  const digits = phone.replace(/[^0-9]/g, '');
  const missing = !name.trim()
    ? 'a name'
    : digits.length < 10
      ? 'a 10-digit phone number'
      : chosen.length === 0
        ? 'at least one market'
        : !kind
          ? 'at least one kind of work'
          : null;
  const committed = proposal.status === 'Committed' || proposal.status === 'Edited_And_Committed';
  const onFile = useOnFileFor(proposal.action_id);

  // After Do it, a Lead gets the send screen right here (item 869). The link is
  // made when the screen opens — straight after Do it, or from the button later.
  const [sendTo, setSendTo] = useState<SendApplicationTarget | null>(null);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const providerId = proposal.target_row_id ?? null;
  const openSend = async () => {
    if (!providerId) return;
    setLinkErr(null);
    try {
      setSendTo({ name: name.trim(), phone: phone.trim(), link: await applicationLinkFor(providerId) });
    } catch (e) {
      setLinkErr(errorMessage(e, 'Could not make their application link.'));
    }
  };
  const wasCommitting = useRef(isCommitting);
  useEffect(() => {
    if (wasCommitting.current && !isCommitting && committed && stage === 'Lead') void openSend();
    wasCommitting.current = isCommitting;
    // openSend reads the current name/phone; it is not a trigger.
  }, [isCommitting, committed, stage]);

  return (
    <CardShell
      title="Add a subcontractor"
      icon={<Truck className="h-4 w-4" />}
      accentClass="border-st-ok-border-strong"
      status={proposal.status}
      committedLabel={`✓ Done. ${name.trim() || 'They'} ${stage === 'Lead' ? 'is in your Pipeline as a Lead.' : 'is in your Pipeline as Interviewing.'}`}
      isCommitting={isCommitting}
      doItDisabled={Boolean(missing)}
      doItDisabledReason={missing ? `Needs ${missing} first.` : undefined}
      onDoIt={() => onDoIt({ name: name.trim(), phone: phone.trim(), coverage_markets: chosen, kind: kind ?? 'nightly', stage }, wasEdited)}
      onDiscard={onDiscard}
      after={committed ? (
        <>
          {stage === 'Lead' && !sent && (
            <div className="rounded-lg border border-tcs-border p-3">
              {sendTo ? (
                <SendApplicationChoice target={sendTo} onDone={() => setSent(true)} />
              ) : (
                <>
                  {linkErr && <p className="mb-1 text-sm text-st-danger-fg">{linkErr}</p>}
                  <button type="button" onClick={() => void openSend()} className="text-sm font-medium text-tcs-text underline">
                    {linkErr ? 'Try again' : 'Send the application'}
                  </button>
                </>
              )}
            </div>
          )}
          <Link to="/people/subcontractors?view=pipeline" className="text-sm font-medium text-st-ok-fg-strong">
            Open Pipeline ›
          </Link>
        </>
      ) : undefined}
    >
      {onFile && !committed && (
        <AlreadyOnFileNotice
          found={onFile}
          busy={isCommitting}
          onAddAnyway={() =>
            onDoIt({ name: name.trim(), phone: phone.trim(), coverage_markets: chosen, kind: kind ?? 'nightly', stage, add_anyway: true }, wasEdited)
          }
        />
      )}
      <p className="text-sm text-tcs-text">
        <b>Here’s what I’ll do:</b>
      </p>
      <ul className="ml-4 list-disc space-y-1 text-sm text-tcs-text">
        <li>
          Add <b>{name.trim() || '…'}</b> as a new subcontractor —{' '}
          {kind === 'both' ? 'nightly cleaning and project work' : kind === 'projects' ? 'project work' : kind === 'nightly' ? 'nightly cleaning' : 'no kind of work yet'},{' '}
          {chosen.length ? chosen.map((m) => m.replace(/\s*Metro$/, '')).join(' + ') : 'no market yet'}
        </li>
        {stage === 'Lead' ? (
          <li>
            Put them in your pipeline as a <b>Lead</b> (they reach Bench once they’re through the application — I won’t skip that)
          </li>
        ) : (
          <li>Put them in your pipeline as <b>Interviewing</b></li>
        )}
        {stage === 'Lead' && <li>Then you send them their application link — your text app, or copy name, phone and link into any app</li>}
      </ul>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Name" />
        <input aria-label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} placeholder="Phone" inputMode="tel" />
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {marketOptions(markets).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={chosen.includes(m)}
            onClick={() => setChosen((c) => (c.includes(m) ? c.filter((x) => x !== m) : [...c, m]))}
            className={`rounded-full border px-2 py-0.5 text-xs ${chosen.includes(m) ? 'border-tcs-text bg-tcs-inverse text-tcs-inverse-ink' : 'border-tcs-border text-tcs-text'}`}
          >
            {m.replace(/\s*Metro$/, '')}
          </button>
        ))}
      </div>
      <div className="mt-2 flex gap-1">
        {([
          ['nightly', 'Nightly cleaning', nightly, setNightly],
          ['projects', 'Project work', projects, setProjects],
        ] as const).map(([k, label, on, set]) => (
          <button
            key={k}
            type="button"
            aria-pressed={on}
            onClick={() => set((v: boolean) => !v)}
            className={`rounded-full border px-2 py-0.5 text-xs ${on ? 'border-tcs-text bg-tcs-inverse text-tcs-inverse-ink' : 'border-tcs-border text-tcs-text'}`}
          >
            {label}
          </button>
        ))}
      </div>
    </CardShell>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';
